import React, { useEffect, useState, useCallback } from 'react';

import DeleteQuotationModal from '../modalContent/DeleteQuotationModal';
import { ConfigContext, Grid } from '../../../base-ui/lib';
import { Box, Link, Stack, Typography } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { useApi } from '../../../hooks/useApi';
import { Quotation } from '../../../types/api/Quotation';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import QuotesCountCard from '../components/QuotesCountCard';
import { toastSuccess } from 'src/Toaster/Toast';
import GridToolbar from './GridToolbar';
import EmptySearchState from '../../../base-ui/Grid/components/EmptySearchState';
import { useQuotationsCount } from '../../../contexts/QuotationsCountContext';

const EmptyStateComponent = () => <EmptySearchState />;

export const QuotesGridList = () => {
	const { config } = React.useContext<any>(ConfigContext);
	const api = useApi();
	const navigate = useNavigate();
	const gridRef = React.useRef<any>(null);
	const { quotesCount, refreshCounts } = useQuotationsCount();

	const [filter, setFilter] = useState('');
	const [createdBy, setCreatedBy] = useState('');
	const [status, setStatus] = useState('');
	const [cardFilter, setCardFilter] = useState(''); // Separate state for card filtering
	const [last30Days, setLast30Days] = useState(false); // New state for 30-day constraint
	const [selectedCardStatus, setSelectedCardStatus] = useState(''); // Card-specific status, separate from toolbar

	// Refresh grid when card filters change
	React.useEffect(() => {
		setTimeout(() => {
			if (gridRef.current?.onRefresh) {
				gridRef.current.onRefresh();
			}

			if (selectedCardStatus || last30Days) {
				refreshCounts();
			}
		}, 0);
	}, [cardFilter, selectedCardStatus, last30Days]);

	// Regular grid options for toolbar filters (original functionality)
	const gridOptions = React.useMemo(() => {
		const options = {} as any;

		if (cardFilter || filter) {
			options.filter = cardFilter || filter;
		}
		if (createdBy) {
			options.createdBy = createdBy;
		}
		if (status) {
			options.status = status;
		}

		return options;
	}, [cardFilter, filter, createdBy, status]);

	// Card overrides - only when cards are active
	const searchAdditionalQueryParams = React.useMemo(() => {
		const params = {} as any;

		// Only override when a card is selected
		if (selectedCardStatus && last30Days) {
			params.status = selectedCardStatus;
			params.last30Days = 'true';
		}

		return params;
	}, [selectedCardStatus, last30Days]);

	const [allData, setAllData] = useState([]); // Store loaded data for dropdowns
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [selectedQuotation, setSelectedQuotation] = useState<Quotation | null>(
		null
	);

	// Keep a ref to the latest refreshCounts function to avoid stale closures
	const refreshCountsRef = React.useRef(refreshCounts);
	React.useEffect(() => {
		refreshCountsRef.current = refreshCounts;
	}, [refreshCounts]);

	useEffect(() => {
		refreshCounts();

		if (gridRef.current?.onRefresh) {
			gridRef.current.onRefresh();
		}
	}, []);

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (!document.hidden) {
				refreshCountsRef.current();
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, []);

	const duplicateQuotation = async (rowData: Quotation) => {
		const quotation: Quotation = { ...rowData };
		if (quotation._id) {
			return await api.quotations.duplicateQuotation(quotation, quotation._id);
		}
	};

	const renderToolbar = useCallback(
		() => (
			<GridToolbar
				creator={createdBy}
				setCreator={setCreatedBy}
				status={status}
				setStatus={setStatus}
				filter={filter}
				setFilter={setFilter}
				handleRefresh={() => gridRef.current?.onRefresh()}
				allData={allData}
				setCardFilter={setCardFilter}
				setLast30Days={setLast30Days}
				setSelectedCardStatus={setSelectedCardStatus}
				hybridMode={false}
			/>
		),
		[
			createdBy,
			setCreatedBy,
			status,
			setStatus,
			filter,
			setFilter,
			allData,
			setCardFilter,
			setLast30Days,
			setSelectedCardStatus,
		]
	);

	const filterList = [
		{
			id: 'projectName',
			label: 'Project Name/ID',
			dataType: 'string',
			defaultValue: '',
		},
		{
			id: 'customerName',
			label: 'Customer Name',
			dataType: 'string',
			defaultValue: '',
		},
		{ id: 'status', label: 'Status', dataType: 'string', defaultValue: '' },
		{
			id: 'firstNameLastName',
			label: 'Created By',
			dataType: 'string',
			defaultValue: '',
		},
	];

	const headCells = [
		{
			field: 'projectName',
			headerName: 'Project Name',
			renderCell: ({ row }) => {
				return (
					<Link
						component={RouterLink}
						to={`${row._id}/options`}
						underline="none"
						sx={{
							cursor: 'pointer',
							fontWeight: 'bold',
						}}
					>
						{row.projectName}
					</Link>
				);
			},
			sortable: true,
			minWidth: 200,
			flex: 2,
		},
		{
			field: 'customerName',
			headerName: 'Customer Name',
			sortable: true,
			minWidth: 180,
			flex: 1.5,
		},
		{
			field: 'companyName',
			headerName: 'Company',
			sortable: true,
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'firstNameLastName',
			headerName: 'Created By',
			sortable: true,
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'createdAt',
			headerName: 'Created At',
			headerAlign: 'center',
			type: 'date',
			align: 'center',
			valueGetter: (value: string | number | Date) => {
				if (!value) return new Date(0);
				const date = new Date(value);
				return isNaN(date.getTime()) ? new Date(0) : date;
			},
			sortable: true,
			minWidth: 120,
			flex: 0.8,
		},
		{
			field: 'status',
			headerName: 'Status',
			sortable: true,
			minWidth: 120,
			flex: 0.7,
		},
		{
			field: 'actions',
			headerName: 'Actions',
			type: 'actions',
			getActions: ({ row }) => [
				<GridActionsCellItem
					key="edit"
					label="Edit"
					onClick={() => navigate(`${row._id}/options`)}
					showInMenu
				/>,
				<GridActionsCellItem
					key="duplicate"
					label="Duplicate"
					onClick={async (e) => {
						e.stopPropagation();
						const duplicatedQuotation = await duplicateQuotation(row);
						toastSuccess({
							description: (
								<Typography variant="body2">
									<Box component="span" fontWeight="bold">
										{row.projectName}
									</Box>{' '}
									has been duplicated.{' '}
									<Link
										component={RouterLink}
										to={`quotations/${duplicatedQuotation._id}/options`}
										color="inherit"
									>
										Edit
									</Link>
								</Typography>
							),
						});
						gridRef.current?.onRefresh();
					}}
					showInMenu
				/>,
				<GridActionsCellItem
					key="delete"
					label="Delete"
					onClick={() => {
						setSelectedQuotation(row);
						setDeleteModalOpen(true);
					}}
					showInMenu
				/>,
			],
			minWidth: 80,
			flex: 0.5,
		},
	];

	return (
		<React.Fragment>
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="stretch"
				sx={{ mb: 2 }}
			>
				{quotesCount.length > 0 ? (
					quotesCount.map((count) => {
						return (
							<QuotesCountCard
								key={count.label}
								label={count.label}
								count={count.count}
								tilesCount={quotesCount.length}
								cardFilter={cardFilter}
								setCardFilter={setCardFilter}
								last30Days={last30Days}
								setLast30Days={setLast30Days}
								selectedCardStatus={selectedCardStatus}
								setSelectedCardStatus={setSelectedCardStatus}
							/>
						);
					})
				) : (
					<Typography variant="h6" color="text.secondary">
						No quotes found
					</Typography>
				)}
			</Stack>

			<Grid
				key={`grid-${selectedCardStatus}-${last30Days}`} // Force re-render when card filters change
				ref={gridRef}
				headCells={headCells}
				filterList={filterList}
				searchEndpont={`${config.uri.base}/quotation`}
				searchAdditionalQueryParams={searchAdditionalQueryParams}
				options={gridOptions}
				pageSizeOptions={[10, 25, 50]}
				selectableRows={false}
				hybridMode={false}
				initialPageSize={50}
				toolbar={renderToolbar}
				showToolbar
				onDataLoad={(data) => {
					setAllData(data);
				}}
				customNoRowsOverlay={EmptyStateComponent}
				sx={{
					// TABLET & IPAD VIEW - Horizontal Scrolling
					'@media (max-width: 1380px)': {
						// TABLE CONTAINER - Enable horizontal scroll
						'& .MuiDataGrid-main': {
							overflowX: 'auto',
							overflowY: 'visible',
							WebkitOverflowScrolling: 'touch',
							scrollbarWidth: 'thin',
						},
						// STICKY ACTIONS COLUMN (stays at right edge)
						'& .MuiDataGrid-columnHeader[data-field="actions"]': {
							position: 'sticky',
							right: 0,
							backgroundColor: 'background.paper',
							zIndex: 5,
						},

						'& .MuiDataGrid-cell[data-field="actions"]': {
							position: 'sticky',
							right: 0,
							backgroundColor: 'background.paper',
							zIndex: 4,
						},

						// Gradient effect for visual depth
						'& .MuiDataGrid-cell[data-field="actions"]::before': {
							content: '""',
							position: 'absolute',
							left: -15,
							top: 0,
							bottom: 0,
							width: '15px',
							background:
								'linear-gradient(to right, transparent, rgba(0,0,0,0.06))',
							pointerEvents: 'none',
						},
					},
				}}
			/>
			{deleteModalOpen && selectedQuotation && (
				<DeleteQuotationModal
					quotation={selectedQuotation}
					modalOpen={deleteModalOpen}
					setModalOpen={setDeleteModalOpen}
					setQuotations={() => gridRef.current?.onRefresh()}
				/>
			)}
		</React.Fragment>
	);
};
